// src/hooks/usePeople.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { PersonModel } from "../models/PersonModel";
import { getPeopleForPane } from "../services/adminPersonService";

const PEOPLE_PAGE_SIZE = 20;

export type PeopleSortOption = "lastEntryAt" | "recentEntryCount";

/**
 * @param autoLoad          Load the first page on mount.
 * @param showRookieEntries Viewer preference. Forwarded to the API so the badge
 *        counts (and the "most active" ordering) exclude rookie-authored entries
 *        when the viewer has them hidden — otherwise the pane advertised entries
 *        the person page never rendered. Mirrors useCustomTopics.
 */
export const usePeople = (autoLoad: boolean = true, showRookieEntries: boolean = true) => {
  const [people, setPeople]           = useState<PersonModel[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [search, setSearch]           = useState("");
  const [sortBy, setSortBy]           = useState<PeopleSortOption>("recentEntryCount");
  // Left-pane country filter (nationality) — null means all countries
  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Kept in a ref so loadPeople stays a stable useCallback while still reading the
  // current preference value (same stale-closure guard used by useCustomTopics).
  const showRookieRef = useRef(showRookieEntries);
  showRookieRef.current = showRookieEntries;

  /**
   * Load first page — replaces the list.
   * Called on initial load, search change, sort change, or manual refresh.
   * When `silent` is true the loading flag is NOT set, so the existing list
   * stays visible while the data refreshes in the background (same behaviour
   * as the matches pane Yenile button).
   */
  const loadPeople = useCallback(async (
    searchTerm: string = search,
    sort: PeopleSortOption = sortBy,
    silent: boolean = false,
    country: string | null = countryFilter
  ) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const result = await getPeopleForPane(0, PEOPLE_PAGE_SIZE, searchTerm, sort, country, showRookieRef.current);
      setPeople(result.content);
      setCurrentPage(0);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch {
      setError("Kişiler yüklenemedi.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, sortBy, countryFilter]);

  // Load on mount — skipped when autoLoad is false so the parent can lazy-load
  // this pane only when the user first switches to it.
  useEffect(() => {
    if (autoLoad) loadPeople();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  // Reload when the viewer flips the "show rookie entries" preference, so the
  // badge counts refresh without requiring a manual Yenile.
  const isFirstPrefRun = useRef(true);
  useEffect(() => {
    if (isFirstPrefRun.current) {
      isFirstPrefRun.current = false;
      return;
    }
    loadPeople(search, sortBy, true, countryFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRookieEntries]);

  /**
   * Append next page to existing list — mirrors loadMoreMatches pattern.
   */
  const loadMorePeople = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (isLoadingMore || nextPage >= totalPages) return;

    setIsLoadingMore(true);
    try {
      const result = await getPeopleForPane(nextPage, PEOPLE_PAGE_SIZE, search, sortBy, countryFilter, showRookieRef.current);
      setPeople(prev => [...prev, ...result.content]);
      setCurrentPage(nextPage);
      setTotalPages(result.totalPages);
    } catch {
      // Silent failure — existing list stays intact
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, totalPages, isLoadingMore, search, sortBy, countryFilter]);

  const handleSearchChange = useCallback((term: string) => {
    setSearch(term);
    loadPeople(term, sortBy);
  }, [sortBy, loadPeople]);

  const handleSortChange = useCallback((newSort: PeopleSortOption) => {
    setSortBy(newSort);
    loadPeople(search, newSort);
  }, [search, loadPeople]);

  /**
   * Left-pane country filter change — resets to page 0 and reloads.
   * Pass null to clear the filter (all countries).
   */
  const handleCountryFilterChange = useCallback((country: string | null) => {
    setCountryFilter(country);
    loadPeople(search, sortBy, false, country);
  }, [loadPeople, search, sortBy]);

  /**
   * Silent refresh — fetches fresh data without triggering the loading state,
   * so the list stays visible during the request (matches pane behaviour).
   */
  const refreshPeople = useCallback(async () => {
    await loadPeople(search, sortBy, true);
  }, [loadPeople, search, sortBy]);

  return {
    people,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    search,
    sortBy,
    countryFilter,
    isLoadingMore,
    loadPeople,
    loadMorePeople,
    handleSearchChange,
    handleSortChange,
    handleCountryFilterChange,
    refreshPeople,
  };
};